// The order sync sends the waiting changes in (#614). Oldest first, except that
// nothing goes before a record it points at that isn't in CRM yet: its parent,
// or a link in its fields (a photo row's visit and visit area, a tick's visit,
// a defect's visit area …), and a photo's upload waits for its area and row.
//
// The queue keeps one entry per record, and a later edit rides on it, so a
// record already waiting keeps its old place when it gains a link to a record
// made after it: a photo row taken while measuring, linked to the condition
// visit started a moment later, would otherwise reach CRM before the visit and
// be refused ("hig_sitevisitid must be a record of the same job"). Pure.

/** An outbox entry as ordering sees it. */
export interface QueuedChange {
  seq?: number;
  kind: string;
  targetId: string;
}

/** What a waiting change points at: a record of the device, and whether CRM has it yet. */
export interface Pointer {
  id: string;
  parentId: string;
  /** Null until CRM has the record (device-sync.ts markRecordSent). */
  etag: string | null;
  fields: Record<string, unknown>;
}

/** The ids a record points at: its parent, and every field holding another record's id. Pure. */
function idsPointedAt(record: Pointer): string[] {
  const values = Object.values(record.fields).filter((v): v is string => typeof v === 'string');
  return [record.parentId, ...values].filter((id) => id !== record.id);
}

/**
 * `entries` in sending order: as queued, but every entry after the queued
 * record entries it depends on (`dependsOn` gives their record ids). A
 * dependency with no record entry waiting is in CRM already, or set aside, and
 * doesn't move anything. A loop (never made by the app) keeps the queued order.
 */
export function sendOrder<E extends QueuedChange>(entries: readonly E[], dependsOn: (entry: E) => readonly string[]): E[] {
  const waitingRecord = new Map(entries.filter((e) => e.kind === 'record').map((e) => [e.targetId, e]));
  const ordered: E[] = [];
  const placed = new Set<E>();
  const placing = new Set<E>();
  const place = (entry: E): void => {
    if (placed.has(entry) || placing.has(entry)) return;
    placing.add(entry);
    for (const id of dependsOn(entry)) {
      const parent = waitingRecord.get(id);
      if (parent && parent !== entry) place(parent);
    }
    placing.delete(entry);
    placed.add(entry);
    ordered.push(entry);
  };
  for (const entry of entries) place(entry);
  return ordered;
}

/**
 * What a waiting change depends on: for a record, the records it points at
 * that CRM doesn't have yet; for a photo's upload or direction, its area and
 * its own row (the row goes first, CLAUDE.md). `record`/`photo` look the
 * device's copies up by id.
 */
export function dependenciesOf(
  entry: QueuedChange,
  record: (id: string) => Pointer | undefined,
  photo: (id: string) => { id: string; areaId: string } | undefined,
): string[] {
  const notInCrm = (id: string) => record(id)?.etag === null;
  if (entry.kind === 'record') {
    const own = record(entry.targetId);
    return own ? idsPointedAt(own).filter(notInCrm) : [];
  }
  if (entry.kind === 'photo' || entry.kind === 'photo-meta') {
    const own = photo(entry.targetId);
    return own ? [own.areaId, own.id].filter(notInCrm) : [];
  }
  return [];
}
